import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, BookOpen, FileText } from "lucide-react";
import EssayPYQBank from "@/components/essay/EssayPYQBank";
import GSPYQBank from "@/components/mains/GSPYQBank";

// slug is the crawlable URL segment (/pyq/<slug>); paper is the API paper code
const TABS = [
  { slug: "essay", paper: null, label: "Essay Paper", short: "Essay" },
  { slug: "gs1", paper: "GS1", label: "GS Paper I", short: "GS-I" },
  { slug: "gs2", paper: "GS2", label: "GS Paper II", short: "GS-II" },
  { slug: "gs3", paper: "GS3", label: "GS Paper III", short: "GS-III" },
  { slug: "gs4", paper: "GS4", label: "GS Paper IV", short: "GS-IV" },
];

const TAB_SEO = {
  essay: {
    title: "UPSC Essay PYQ (2013-2026) — Themes & Analysis | Clarias",
    desc: "Browse 14 years of UPSC Essay Mains previous-year questions mapped into 18 themes with full writing roadmaps. Free.",
    intro: "Every UPSC Essay Paper previous-year question from 2013 to 2026, grouped into Philosophical and Issue-Based themes. Each essay comes with the question demand, the dimensions UPSC expects, and a clear opening-body-conclusion roadmap.",
  },
  gs1: {
    title: "UPSC GS Paper 1 PYQ (2013-2026) — Chapterwise | Clarias",
    desc: "Every UPSC GS-I Mains previous-year question (2013-2026), mapped chapter & subtopic-wise with answer approach. Free.",
    intro: "Every UPSC General Studies Paper I (History, Art & Culture, Geography, Society) Mains previous-year question from 2013 to 2026, arranged chapter and subtopic-wise with what UPSC is testing and how to approach each answer.",
  },
  gs2: {
    title: "UPSC GS Paper 2 PYQ (2013-2026) — Chapterwise | Clarias",
    desc: "Every UPSC GS-II Mains previous-year question (2013-2026), mapped chapter & subtopic-wise with answer approach. Free.",
    intro: "Every UPSC General Studies Paper II (Polity, Governance, Constitution, Social Justice, International Relations) Mains previous-year question from 2013 to 2026, arranged chapter and subtopic-wise with model answer approaches.",
  },
  gs3: {
    title: "UPSC GS Paper 3 PYQ (2013-2026) — Chapterwise | Clarias",
    desc: "Every UPSC GS-III Mains previous-year question (2013-2026), mapped chapter & subtopic-wise with answer approach. Free.",
    intro: "Every UPSC General Studies Paper III (Economy, Agriculture, Science & Tech, Environment, Security, Disaster Management) Mains previous-year question from 2013 to 2026, arranged chapter and subtopic-wise with answer approaches.",
  },
  gs4: {
    title: "UPSC GS Paper 4 Ethics PYQ (2013-2026) — Case Studies | Clarias",
    desc: "Every UPSC GS-IV Ethics Mains previous-year question & case study (2013-2026), mapped with answer approach. Free.",
    intro: "Every UPSC General Studies Paper IV (Ethics, Integrity & Aptitude) Mains previous-year question and case study from 2013 to 2026, arranged theme-wise with the ethical dimensions and a structured answer approach.",
  },
};

const setMeta = (attr, key, content) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

const PublicPYQExplorer = () => {
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();
  const valid = TABS.find((t) => t.slug === tabParam);
  const [slug, setSlug] = useState(valid ? valid.slug : "essay");

  // keep state in sync when the URL param changes (back/forward, direct link)
  useEffect(() => {
    const v = TABS.find((t) => t.slug === tabParam);
    setSlug(v ? v.slug : "essay");
  }, [tabParam]);

  // SEO tags per active tab
  useEffect(() => {
    const seo = TAB_SEO[slug] || TAB_SEO.essay;
    document.title = seo.title;
    setMeta("name", "description", seo.desc);
    setMeta("property", "og:title", seo.title);
    setMeta("property", "og:description", seo.desc);
    setCanonical(`https://clarias.in/pyq/${slug}`);
  }, [slug]);

  const active = TABS.find((t) => t.slug === slug) || TABS[0];
  const seo = TAB_SEO[slug] || TAB_SEO.essay;
  const goSignup = () => navigate("/auth");
  const selectTab = (s) => { setSlug(s); navigate(`/pyq/${s}`); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100/40" data-testid="public-pyq-explorer">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full mb-4">
            <Sparkles className="w-3.5 h-3.5" /> 100% free · no sign-up needed to browse
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">
            UPSC Mains PYQ Explorer
          </h1>
          <p className="text-base text-slate-600 mt-3 max-w-2xl">
            Fourteen years of UPSC Mains previous-year questions (2013–2026) — Essay + GS Papers I–IV —
            mapped topic-wise and decoded with what UPSC is testing and how to approach each answer.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <Button onClick={goSignup} className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6" data-testid="public-pyq-signup-cta">
              Start practising free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <span className="text-sm text-slate-400">1,200+ questions · fully analysed</span>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-2 sm:px-6 flex gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((tb) => {
            const isActive = slug === tb.slug;
            const Icon = tb.slug === "essay" ? BookOpen : FileText;
            return (
              <button
                key={tb.slug}
                onClick={() => selectTab(tb.slug)}
                className={`flex items-center gap-1.5 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  isActive ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`public-pyq-tab-${tb.slug}`}
              >
                <Icon className="w-4 h-4" /> <span className="hidden sm:inline">{tb.label}</span><span className="sm:hidden">{tb.short}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* SEO intro copy (crawlable) */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <p className="text-sm text-slate-500 leading-relaxed" data-testid="public-pyq-intro">{seo.intro}</p>
      </div>

      {/* Bank content */}
      <div>
        {active.paper === null ? (
          <EssayPYQBank onPractice={goSignup} />
        ) : (
          <GSPYQBank key={active.paper} paper={active.paper} onPractice={goSignup} />
        )}
      </div>

      {/* Footer CTA */}
      <div className="border-t border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 text-center">
          <h2 className="font-heading text-xl font-bold text-slate-900">Ready to actually write these answers?</h2>
          <p className="text-sm text-slate-500 mt-1">Create a free Clarias account to practise any PYQ and get instant, structured evaluation.</p>
          <Button onClick={goSignup} className="mt-4 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6" data-testid="public-pyq-footer-cta">
            Get started free <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PublicPYQExplorer;
